/**
 * Centralized Search Parameters Schema
 *
 * Single source of truth for all query parameters in the application.
 * Routes import their schemas from here, and security monitoring derives
 * allowed parameters from these same schemas.
 */

import { z } from "zod";

/**
 * Root route search parameters
 * Used by: src/routes/__root.tsx
 */
export const rootSearchSchema = z.object({
  embed: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((val) => {
      if (typeof val === "string") {
        return val === "true";
      }
      return val;
    }),
  colorScheme: z.enum(["light", "dark"]).optional(),
  width: z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (typeof val === "number") {
        return val >= 320 && val <= 1000 ? val : undefined;
      }
      if (val === "mobile" || val === "tablet") {
        return val;
      }
      const num = Number.parseInt(val, 10);
      if (!Number.isNaN(num) && num >= 320 && num <= 1000) {
        return num;
      }
      return undefined;
    })
    .optional(),
  height: z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (typeof val === "number") {
        return val >= 480 && val <= 1200 ? val : undefined;
      }
      if (val === "mobile" || val === "tablet") {
        return val;
      }
      const num = Number.parseInt(val, 10);
      if (!Number.isNaN(num) && num >= 480 && num <= 1200) {
        return num;
      }
      return undefined;
    })
    .optional(),
  currency: z.enum(["USD", "KRW", "JPY"]).optional(),
  locale: z.enum(["en-US", "ko-KR", "ja-JP"]).optional(),
});

/**
 * DeFi portfolio search parameters
 * Used by: src/routes/_layout/defi.portfolio.tsx
 */
export const portfolioSearchSchema = z.object({
  protocolKey: z.string().optional(),
  position: z.string().optional(),
});

/**
 * DeFi stablecoins search parameters
 * Used by: src/routes/_layout/defi.stablecoins.index.tsx
 */
export const stablecoinsSearchSchema = z.object({
  protocolKey: z.string().optional(),
});

/**
 * DeFi pool detail search parameters
 * Used by: src/routes/_layout/defi.pool.$tokenAddress.$protocolId.tsx
 */
export const poolDetailSearchSchema = z.object({
  protocolKey: z.string().optional(),
  position: z.enum(["create", "manage-supply", "manage-withdraw"]).optional(),
});

/**
 * Swap search parameters
 * Used by: src/routes/_layout/swap.tsx
 */
export const swapSearchSchema = z.object({
  fromChainId: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => (val !== undefined ? String(val) : undefined)),
  fromTokenAddress: z
    .string()
    .optional()
    .transform((val) => val?.toLowerCase()),
  toChainId: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => (val !== undefined ? String(val) : undefined)),
  toTokenAddress: z
    .string()
    .optional()
    .transform((val) => val?.toLowerCase()),
});

/**
 * Extract all allowed parameter names from a Zod schema
 */
function getSchemaKeys<T extends z.ZodRawShape>(schema: z.ZodObject<T>): string[] {
  return Object.keys(schema.shape);
}

/**
 * All allowed query parameters - derived automatically from the schemas above.
 * This is used by the security monitoring to detect unknown parameters.
 */
export const ALLOWED_QUERY_PARAMETERS: readonly string[] = [
  ...new Set([
    ...getSchemaKeys(rootSearchSchema),
    ...getSchemaKeys(portfolioSearchSchema),
    ...getSchemaKeys(stablecoinsSearchSchema),
    ...getSchemaKeys(poolDetailSearchSchema),
    ...getSchemaKeys(swapSearchSchema),
  ]),
];
